/** Every photo on an event – the gallery when there is one, otherwise the single cover photo. */
export const eventImages = (ev) => (ev?.images?.length ? ev.images : ev?.image ? [ev.image] : []);

/** YouTube / Vimeo link → embed + poster. Null for anything else. */
export const videoEmbed = (url) => {
  if (!url) return null;
  const yt = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|live\/|embed\/)|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return { provider: "youtube", id: yt[1], embed: `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1`, thumb: `https://i.ytimg.com/vi/${yt[1]}/hqdefault.jpg` };
  const vm = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vm) return { provider: "vimeo", id: vm[1], embed: `https://player.vimeo.com/video/${vm[1]}?autoplay=1&dnt=1`, thumb: null };
  return null;
};

/** Shrink a photo in the browser before upload: longest side ≤ maxSide, WebP where the browser can encode it. */
export const compressImage = async (file, { maxSide = 2400, quality = 0.86, skipBelow = 900 * 1024 } = {}) => {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  let bitmap;
  try { bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { return file; }
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size <= skipBelow) { bitmap.close?.(); return file; }
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const toBlob = (type) => new Promise((res) => canvas.toBlob(res, type, quality));
  let blob = await toBlob("image/webp");
  if (!blob || blob.type !== "image/webp") blob = await toBlob(file.type === "image/png" ? "image/png" : "image/jpeg");
  if (!blob || (scale === 1 && blob.size >= file.size)) return file;
  const ext = blob.type === "image/webp" ? "webp" : blob.type === "image/png" ? "png" : "jpg";
  return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}.${ext}`, { type: blob.type });
};

/** Where the event is in time, for the countdown pill. */
export const countdownState = (ev, now = new Date()) => {
  const timed = !!ev.start_time;
  const start = new Date(`${ev.date}T${ev.start_time || "00:00"}:00`);
  const end = new Date(`${ev.end_date || ev.date}T${ev.end_time || "23:59"}:59`);
  if (Number.isNaN(start.getTime())) return { state: "soon", label: "" };
  if (now > end) return { state: "over", label: "Avslutta" };
  const sameDay = start.toDateString() === now.toDateString();
  if (now >= start || (!timed && sameDay)) return { state: "live", label: "Pågår no" };
  const min = Math.floor((start - now) / 60000);
  const h = Math.floor(min / 60);
  const d = Math.floor(h / 24);
  const short = h >= 1 ? `om ${h} t ${min % 60} min` : `om ${min} min`;
  if (sameDay) return { state: "today", label: `I dag kl. ${ev.start_time}`, detail: short };
  if (d >= 14) return { state: "soon", label: `om ${d} dagar` };
  if (d >= 1) return { state: "soon", label: `om ${d} ${d === 1 ? "dag" : "dagar"} ${h % 24} t` };
  return { state: "soon", label: short };
};
